import { PaginationInterfaceLF } from ".";
import { Bundle, BundleUnitByIdInterface, Currency, Portion } from "./common";

export enum GroupTheme {
  NumouNow = "NumouNow",
  Balanced = "Balanced",
  Keto = "Keto",
  GlutenFree = "GlutenFree",
  DairyFree = "DairyFree",
  Pregnancy = "Pregnancy",
  LowCarb = "LowCarb",
  Vegetarian = "Vegetarian",
  Slims = "Slims",
  Lactating = "Lactating",
  NumouFit = "NumouFit",
  Custom = "Custom",
}

export enum LanguageLocale {
  ar = "ar",
  en = "en",
  ar_KW = "ar-KW",
  en_KW = "en-KW",
  ar_SA = "ar-SA",
  en_SA = "en-SA",
}

export interface Group {
  id: number;
  storeId: number;
  name: string | null;
  description: string | null;
  iconUrl: string | null;
  blogUrl: null | null;
  priceLabel: string | null;
  theme: GroupTheme;
  tag: string | null;
  exclusiveAccess: boolean;
  sort: number;
  groupAppearanceId: number | null;
  displayMenuForPackageId: number | null;
  nutritionCategoryId: number | null;
}

export interface LocalizedGroup {
  storeId: number;
  groupId: number;
  locale: LanguageLocale;
  name: string;
  description: string;
  iconUrl: string;
  blogUrl: string;
  priceLabel: string;
  tag: string;
}

export interface GroupPayload {
  name: string;
  description: string;
  iconUrl: string;
  blogUrl: string;
  priceLabel: string;
  theme: GroupTheme;
  exclusiveAccess: boolean;
  tag: string;
  sort: number;
  groupAppearanceId?: number;
  displayMenuForPackageId: number | null;
  nutritionCategoryId: number | null;
}

export interface LocalizedGroupPayload extends Omit<
  LocalizedGroup,
  "storeId" | "groupId"
> {}

export interface GetGroupsAPIResponse {
  data: Group[];
  pagination: PaginationInterfaceLF;
}

export interface PostGroupAPIResponse {
  data: Group;
}

export interface GetLocalizedGroupsAPIResponse {
  data: LocalizedGroup[];
  pagination: PaginationInterfaceLF;
}

export interface PostLocalizedGroupsAPIResponse {
  data: LocalizedGroup;
}

export interface DeleteLocalizedGroupsAPIResponse {
  data: {
    message: string;
  };
}

export interface Validities {
  fromDate: Date;
  untilDate: Date;
  isActive: boolean;
}

export interface Structure {
  displayName: string;
  meal: string;
  localizations: {
    locale: string;
    displayName: string;
  }[];
}

// create new interface which extends Group , but removed `id` and adds `groupId`
export interface PackageGroupInterface extends Omit<Group, "id"> {
  groupId: number;
}
export interface PackagesInterface {
  id: number;
  originalTitle: string;
  storeId: number;
  originalDescription: string;
  type: string;
  numberOfMeals: number;
  numberOfSnacks: number;
  maximumOffDays: number;
  canBreakfastExecluded: boolean;
  groupId: number;
  group?: PackageGroupInterface; // Added from included in FE
  iconUrl: string;
  imageUrl: string;
  restriction: string;
  packageClass?: string;
  structure?: Structure[];
  validities?: Validities[];
  offDays: string[];
}

export interface CreatePackagePayload {
  brandId: number;
  originalTitle: string;
  originalDescription: string;
  type: string;
  numberOfMeals?: number;
  numberOfSnacks?: number;
  maximumOffDays?: number;
  canBreakfastExecluded?: boolean;
  packageClass?: string;
  groupId: number;
  restriction?: string;
  structure?: Structure[];
  validities?: Validities[];
  offDays: string[];
}

export enum PackageType {
  Meal = "Meal",
  Point = "Point",
  Macro = "Macro",
}

export enum PortionFilter {
  Servings = "Servings",
  Macronutrients = "Macronutrients",
}

export interface PostBundlePayload {
  status: string; //'Active' | 'Inactive';
  purpose: string; // "Commercial" | 'Promotional'
  numberOfDays: number;
}

export interface PostBundle extends PostBundlePayload {
  id: number;
}

export interface GetPackageByStoreApiResponse {
  data: PackagesInterface[];
  pagination: PaginationInterfaceLF;
  included: {
    groups: PackageGroupInterface[];
  };
}

export interface PostBundleApiResponse {
  data: PostBundle;
}

export interface GetBundleByStoreAndPackageIdResponse {
  data: Bundle[];
  pagination: PaginationInterfaceLF;
}

export interface GetBundleUnitsByStoreAndPackageIdResponse {
  data: BundleUnitByIdInterface[];
  pagination: PaginationInterfaceLF;
  included: {
    currencies: Currency[];
    portions: Portion[];
  };
}

export interface PostBundleUnitApiResponse {
  data: BundleUnitByIdInterface;
  included: {
    currency: Currency;
    portion: Portion;
  };
}

export interface CreditPackageInterface {
  id: number;
  price: number;
  points: number;
  validityDays: number;
  title: string;
  description: string;
  iconUrl: string | null;
  refundable: boolean;
  deliveryLimit: number;
  storeId: number;
  accessType: "Public" | "Private";
  currencyId: number;
  currencyCode?: string;
}

export interface CreditPackagePayload {
  price: number;
  points: number;
  validityDays: number;
  title: string;
  description: string;
  iconUrl: string | null;
  refundable: boolean;
  accessType: "Public" | "Private";
}

export interface GetCreditPackageApiResponse {
  data: CreditPackageInterface[];
  pagination: PaginationInterfaceLF;
  included: {
    currencies: Currency[];
  };
}

export interface PostCreditPackageApiResponse {
  data: CreditPackageInterface;
  included: {
    currencies: Currency[];
  };
}
export interface PortionData {
  id: number;
  title: string;
  style: string;
  protein: number;
}
export interface PortionListApiResponse {
  data: PortionData[];
}
export interface CoreMacrosOptionsData {
  id: number;
  storeId: number;
  maximumNoOfMeals: number;
  minimumNoOfMeals: number;
  maximumNoOfSnacks: number;
  minimumNoOfSnacks: number;
  days: number[];
  minimumProtein: number;
  maximumProtein: number;
  proteinStepValue: number;
  minimumCarbs: number;
  maximumCarbs: number;
  carbsStepValue: number;
  defaultNoOfMeal: number;
  defaultNoOfSnack: number;
  defaultDays: number;
  defaultProtein: number;
  defaultCarbs: number;
}
export interface CorePackagePrice {
  bundleId: number;
  unitId: number;
  numberOfDays: number;
  price: number;
  discountedPrice: number;
  currencyCode: string;
  currencyId: number;
  packageId: number;
  maximumOffDays: number;
  offDays: number[];
  canBreakfastExecluded: boolean;
  structure: string[];
  structureDisplayName?: string[];
}

export interface GetCorePackageApiResponse {
  data: CoreMacrosOptionsData;
}

export interface GetCorePricingApiResponse {
  data: CorePackagePrice;
}

export interface PostCorePricingApiResponse {
  numberOfDays: number;
  numberOfMeals: number;
  numberOfSnacks: number;
  canBreakfastExecluded?: boolean;
  meals: {
    meal: string | null;
    carbs?: number;
    proteins?: number;
  }[];
}
interface MacroMealData {
  id: number;
  type: string;
  protein?: number;
  carbs?: number;
}
export interface UpdateMacroMealsPayload {
  meals: MacroMealData[];
}

export interface GetPackageByIdApiResponse {
  data: PackagesInterface;
}
export interface GroupThemeColors {
  id: number;
  storeId: number;
  name: string;
  configuration: {
    packageCard: {
      backgroundColor: string;
      textColor: string;
    };
    sponsoredCard: {
      borderColor: string;
      textColor: string;
    };
    subscriptionCard: {
      backgroundColor: string;
      titleColor: string;
      button: {
        backgroundColor: string;
        textColor: string;
      };
    };
  };
}
export interface GetGroupThemesApiResponse {
  data: GroupThemeColors[];
}
export interface PostGroupThemesApiResponse {
  data: GroupThemeColors;
}

export interface GetNutritionCategoriesAPIResponse {
  data: NutritionCategory[];
}

export interface NutritionCategory {
  id: number;
  name: string;
  imageUrl: string;
  localizations: Localization[];
  description: string | null;
}

export interface Localization {
  locale: string;
  name: string;
  imageUrl: string;
  description: string | null;
}

export interface PostNutritionCategoryAPIResponse {
  data: NutritionCategory;
}
